import { z } from 'zod'

/**
 * 장례식장 (BIZ-90 S1) — 자유텍스트 장소명을 엔티티로.
 *
 * 정본 데이터는 kbugo(모두부고, 자사) 명부 import + 운영자 등록. 주문서·파서가 넘긴 장소명은
 * 서버가 정규화해 name·aliases와 **정확 일치**로만 연결한다(포함 매칭 금지 — "OO병원"이
 * "OO병원장례식장"에 잘못 붙는 오매칭을 막는다). 매칭이 안 되면 자유 입력 그대로 둔다.
 *
 * - isPartner: 디지털 화환 디스플레이 제휴(BIZ-125). 애드온 노출 판정의 유일한 근거.
 * - wreathAccepted: 식장 단위 화환 취급 여부. 차단 강제(카탈로그·접수)는 S3.
 */

export const funeralHallSource = z.enum(['KBUGO', 'MANUAL'])
export type FuneralHallSource = z.infer<typeof funeralHallSource>

/** 주문서·검색이 보는 요약. 좌표·연락처 같은 운영 필드는 뺀다. */
export const funeralHallSummary = z.object({
  id: z.number().int(),
  name: z.string(),
  address: z.string().nullable(),
  isPartner: z.boolean(),
  wreathAccepted: z.boolean(),
})
export type FuneralHallSummary = z.infer<typeof funeralHallSummary>

export const funeralHallSearchResponse = z.object({
  items: z.array(funeralHallSummary),
})
export type FuneralHallSearchResponse = z.infer<typeof funeralHallSearchResponse>

/** 장소명 → 장례식장 연결. 못 찾으면(또는 애매하면) hall=null. */
export const funeralHallResolveResponse = z.object({
  hall: funeralHallSummary.nullable(),
})
export type FuneralHallResolveResponse = z.infer<typeof funeralHallResolveResponse>

// ── 운영자 관리 ─────────────────────────────────────────────

export const funeralHallView = funeralHallSummary.extend({
  aliases: z.array(z.string()),
  addressDetail: z.string().nullable(),
  zipcode: z.string().nullable(),
  lat: z.number().nullable(),
  lng: z.number().nullable(),
  phone: z.string().nullable(),
  kbugoCode: z.string().nullable(),
  source: funeralHallSource,
  isActive: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
})
export type FuneralHallView = z.infer<typeof funeralHallView>

export const funeralHallFilter = z.enum(['all', 'partner', 'noWreath', 'inactive'])
export type FuneralHallFilter = z.infer<typeof funeralHallFilter>

export const funeralHallListResponse = z.object({
  items: z.array(funeralHallView),
  /** 필터·검색에 맞는 전체 건수(items는 limit까지만). */
  total: z.number().int(),
})
export type FuneralHallListResponse = z.infer<typeof funeralHallListResponse>

/** 빈소(호실). 주문서가 타이핑 대신 고르게 하는 보조 목록 — 자유 입력은 그대로. */
export const funeralHallRoom = z.object({
  id: z.number().int(),
  name: z.string(),
})
export type FuneralHallRoom = z.infer<typeof funeralHallRoom>

export const funeralHallResponse = z.object({
  hall: funeralHallView,
  rooms: z.array(funeralHallRoom),
  /** 이 식장에서 차단된 상품 id(S3). 주문서가 카탈로그를 거르고 고른 상품을 재검사한다. */
  blockedProductIds: z.array(z.number().int()),
})
export type FuneralHallResponse = z.infer<typeof funeralHallResponse>

// ── 차단 상품 (S3, 운영자) ──────────────────────────────────

export const funeralHallBlock = z.object({
  productId: z.number().int(),
  productName: z.string(),
  reason: z.string().nullable(),
  createdAt: z.string(),
})
export type FuneralHallBlock = z.infer<typeof funeralHallBlock>

export const funeralHallBlockListResponse = z.object({ items: z.array(funeralHallBlock) })
export type FuneralHallBlockListResponse = z.infer<typeof funeralHallBlockListResponse>

/** 차단 목록 전체 교체(멱등). 빈 배열이면 전부 해제. */
export const setFuneralHallBlocksRequest = z.object({
  items: z
    .array(
      z.object({
        productId: z.number().int().positive(),
        reason: z.string().trim().max(200).nullable().optional(),
      }),
    )
    .max(200),
})
export type SetFuneralHallBlocksRequest = z.infer<typeof setFuneralHallBlocksRequest>

function optionalText(max: number) {
  return z.preprocess(
    (v) => (typeof v === 'string' && v.trim() === '' ? null : v),
    z.string().trim().max(max).nullable().optional(),
  )
}

export const upsertFuneralHallRequest = z.object({
  name: z
    .string()
    .trim()
    .min(2, { message: '장례식장 이름을 입력하세요' })
    .max(100, { message: '이름은 100자 이하여야 합니다' }),
  // 빈 문자열·중복은 서버가 걸러 저장한다(줄 단위 입력 UI를 관대하게).
  aliases: z
    .array(z.string().trim().max(100))
    .max(20, { message: '별칭은 20개까지입니다' })
    .optional(),
  address: optionalText(300),
  addressDetail: optionalText(200),
  zipcode: optionalText(10),
  phone: optionalText(30),
  wreathAccepted: z.boolean().optional(),
  isPartner: z.boolean().optional(),
  isActive: z.boolean().optional(),
})
export type UpsertFuneralHallRequest = z.infer<typeof upsertFuneralHallRequest>

/** 수정은 부분 갱신 — 보낸 필드만 바뀐다(토글 한 개만 보내는 admin 행 액션용). */
export const updateFuneralHallRequest = upsertFuneralHallRequest.partial()
export type UpdateFuneralHallRequest = z.infer<typeof updateFuneralHallRequest>

export const FuneralHallErrorCode = {
  FUNERAL_HALL_NOT_FOUND: 'FUNERAL_HALL_NOT_FOUND',
  /** 같은 이름+주소가 이미 있다. */
  FUNERAL_HALL_DUPLICATE: 'FUNERAL_HALL_DUPLICATE',
} as const
